/**
 * Decide whether to show a mention toast after a poll.
 *
 * The toast only announces NEW direct mentions: a mention notification whose
 * comment id is newer than the last one this browser already toasted (the
 * persisted cursor). Existing unread mentions never re-toast on reload, and
 * acknowledged (read) mentions drop out of `latest_mention_notification`
 * server-side.
 */
export interface MentionUnreadCounts {
  totalCount: number;
  mentionCount: number;
  mentionedUnreadCount: number;
  latestMentionNotification?: MentionNotificationTarget | null;
}

export interface MentionNotificationTarget {
  conversationId: string | number;
  commentId?: string | number | null;
  reason?: string | null;
}

interface RawMentionNotificationTarget {
  conversation_id?: string | number | null;
  comment_id?: string | number | null;
  reason?: string | null;
}

interface RawMentionUnreadCounts {
  total?: unknown;
  by_reason?: { mention?: unknown } | null;
  mentioned_unread_total?: unknown;
  latest_mention_notification?: RawMentionNotificationTarget | null;
}

export interface MentionToastDecision {
  shouldShow: boolean;
  nextLastSeenCommentId: number | null;
}

export function parseMentionCursor(value: unknown): number | null {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

export function resolveMentionToast(
  lastSeenCommentId: number | null,
  latestMentionNotification: MentionNotificationTarget | null | undefined,
): MentionToastDecision {
  const commentId = parseMentionCursor(latestMentionNotification?.commentId);
  if (!latestMentionNotification || commentId == null) {
    return { shouldShow: false, nextLastSeenCommentId: lastSeenCommentId };
  }

  if (lastSeenCommentId != null && commentId <= lastSeenCommentId) {
    return { shouldShow: false, nextLastSeenCommentId: lastSeenCommentId };
  }

  return { shouldShow: true, nextLastSeenCommentId: commentId };
}

export function normalizeUnreadCounts(
  data: RawMentionUnreadCounts | null | undefined,
): MentionUnreadCounts {
  const totalCount = Number(data?.total ?? 0);
  const mentionCount = Number(data?.by_reason?.mention ?? 0);
  const mentionedUnreadCount = Number(data?.mentioned_unread_total ?? 0);
  const notification = data?.latest_mention_notification;

  return {
    totalCount: Number.isFinite(totalCount) ? Math.max(0, totalCount) : 0,
    mentionCount: Number.isFinite(mentionCount) ? Math.max(0, mentionCount) : 0,
    mentionedUnreadCount: Number.isFinite(mentionedUnreadCount)
      ? Math.max(0, mentionedUnreadCount)
      : 0,
    latestMentionNotification:
      notification && notification.conversation_id
        ? {
            conversationId: notification.conversation_id,
            commentId: notification.comment_id,
            reason: notification.reason,
          }
        : null,
  };
}
